import { TRIAL_PLAN_TYPES } from '@gonfalon/billing/constants';
import {
  getTrialStatusInfo,
  hasPaidPlan,
  hasSubscription,
  isActiveTrialWithNoPlan,
  isTrial,
  isTrialExpired,
} from '@gonfalon/billing/subscriptions';
// oxlint-disable-next-line no-restricted-imports
import type { LimitNames, ModernSubscription, PlanLimitsType, Subscription } from '@gonfalon/billing/types';
import { BillingIntervals, PlanTypes } from '@gonfalon/billing/types';
import { getLimitDisplayName, hasSubscriptionLimit } from '@gonfalon/billing/utils';
import {
  disableFreeDevPlanSelection,
  enableDeveloperFacade,
  enableSelfServeObservability,
  enableAgentControlCheckout,
} from '@gonfalon/dogfood-flags/dogfood-flags';
import { formatNumber } from '@gonfalon/format/formatNumber';
import type { schemas } from '@gonfalon/openapi/types';
import { capitalize } from '@gonfalon/strings/capitalize';
import { startOfTomorrow } from 'date-fns';

import { CENTS_PER_DOLLAR } from './formatters';
import {
  developer2025Plan,
  foundation2025Plan,
  foundationPlan,
  guardianPlan,
  OBSERVABILITY_VERSION,
  planIdToPlan,
  VERSION,
} from './planData';
import { developerPlan } from './planData/developerPlan';
import type {
  AnnualCommitOverageProductDetails,
  BulkPricingTier,
  Plan,
  ProductVisibilityContext,
  ResolvedVersion,
} from './types';

// Fallback version with no billable products. Used when no plan is selected yet
// or when a plan/version has no products to price (e.g., Guardian).
export const EMPTY_VERSION: Readonly<ResolvedVersion> = { products: [], versionKey: undefined };

/**
 * Returns the version key to send in API requests.
 * Only Foundation plans need an explicit version;
 * the backend defaults to the correct version for other plan types.
 */
// Only Foundation version keys are sent to the backend. Developer2025 default version is enforced by the backend.
export const getVersionForRequest = (
  planType: PlanTypes | null | undefined,
  versionKey: number | undefined,
): number | undefined => (enableAgentControlCheckout() && isFoundationPlanNotFacade(planType) ? versionKey : undefined);

/**
 * Resolves a versionKey to a valid key for the given plan.
 * If versionKey is provided and exists in the plan, returns it.
 * Otherwise falls back to the plan's flag-based default.
 */
const resolvePlanVersionKey = (plan: Plan, versionKey?: number): number => {
  if (enableAgentControlCheckout() && versionKey !== undefined && plan.versions[versionKey]) {
    return versionKey;
  }

  const showObservabilityVersion = plan.useObservabilityFlagForVersion && enableSelfServeObservability();
  return showObservabilityVersion ? OBSERVABILITY_VERSION : VERSION;
};

/**
 * Returns version details for a plan at the given key.
 * Delegates to resolvePlanVersionKey for fallback logic.
 */
export const getPlanVersion = (plan: Plan, versionKey?: number): ResolvedVersion => {
  const resolvedKey = resolvePlanVersionKey(plan, versionKey);
  const version = plan.versions[resolvedKey];
  return version ? { ...version, versionKey: resolvedKey } : EMPTY_VERSION;
};

/**
 * Check if a plan is the legacy Developer plan facade.
 * Falls back to false if the property is not set.
 */
export const isDeveloperPlanFacade = (plan: Plan): boolean => {
  return plan.isLegacyDeveloperFacade ?? false;
};

/**
 * Get the display name for a plan, taking into account feature flags.
 * When enableDeveloperFacade is enabled, certain plans
 * display as "Developer / Foundation" for legacy compatibility:
 * - Plans marked as legacy Developer facade (isLegacyDeveloperFacade: true)
 * - Foundation2023 plans with monthly billing
 *
 * When enableDeveloperFacade is disabled, the actual plan name is shown.
 */
export const getPlanDisplayName = (plan: Plan, billingInterval?: BillingIntervals): string => {
  if (!enableDeveloperFacade()) {
    return plan.marketingCopy.name;
  }

  const isLegacyDeveloperDisplay =
    isDeveloperPlanFacade(plan) ||
    (plan.planType === PlanTypes.FOUNDATION2023 && billingInterval === BillingIntervals.MONTHLY);

  return isLegacyDeveloperDisplay ? 'Developer / Foundation' : plan.marketingCopy.name;
};

/**
 * Check if a plan has configurable limits by looking it up in planData.
 * Fixed plans (e.g., Developer, Developer2025) return false.
 * Configurable plans (e.g., Foundation2023, Foundation2025) return true.
 * Falls back to true if plan is not found (for legacy plans).
 */
export const hasConfigurableLimits = (planType: PlanTypes): boolean => {
  // Find the plan in planData by matching planType
  const plan = Object.values(planIdToPlan).find((p) => p.planType === planType);

  // If plan is found, use its hasConfigurableLimits property
  // Otherwise, assume it might need limits fetched (legacy behavior)
  return plan?.hasConfigurableLimits ?? true;
};

/**
 * Check if a plan is a Foundation-tier plan (requires payment card UI).
 * Foundation-tier plans include: Foundation2023, Foundation2025, and legacy Developer (which maps to Foundation2023).
 * Falls back to false if plan is not found (for legacy plans without metadata).
 */
export const isFoundationPlan = (planType: PlanTypes): boolean => {
  // Find the plan in planData by matching planType
  const plan = Object.values(planIdToPlan).find((p) => p.planType === planType);

  // If plan is found, use its isFoundationPlan property
  // Otherwise, assume it's not a Foundation plan (legacy behavior)
  return plan?.isFoundationPlan ?? false;
};

/**
 * Check if a plan is an actual Foundation plan (Foundation2023 or Foundation2025).
 * This excludes the legacy Developer plan facade, which is treated as Foundation for payment UI
 * but should not be included in Foundation-specific logic like customize commits flow.
 */
export const isFoundationPlanNotFacade = (planType: PlanTypes | null | undefined): boolean => {
  if (!planType) {
    return false;
  }

  const plan = Object.values(planIdToPlan).find((p) => p.planType === planType);
  if (!plan) {
    return false;
  }

  return isFoundationPlan(planType) && !isDeveloperPlanFacade(plan);
};

/**
 * Check if a plan is a free developer plan (no payment card required).
 * Currently, only Developer2025 is a free developer plan.
 * Falls back to false if plan is not found or planType is null.
 */
export const isFreeDevPlan = (planType: PlanTypes | null | undefined): boolean => {
  // Return false if planType is not provided
  if (!planType) {
    return false;
  }

  // Find the plan in planData by matching planType
  const plan = Object.values(planIdToPlan).find((p) => p.planType === planType);

  // If plan is found, use its isFreeDevPlan property
  // Otherwise, assume it's not a free dev plan (legacy behavior)
  return plan?.isFreeDevPlan ?? false;
};

/**
 * True when the subscription is on a trial plan type.
 */
export const isTrialPlan = (planType: PlanTypes | null | undefined): boolean => {
  if (!planType) {
    return false;
  }
  return TRIAL_PLAN_TYPES.has(planType);
};

/**
 * Check if a plan signup should be tracked as a developer signup (for analytics).
 * Developer plans include: Developer (legacy) and Developer2025.
 * Falls back to false if plan is not found or planId is null/undefined.
 */
export const isDeveloperSignupPlan = (planId: string | null | undefined): boolean => {
  // Return false if planId is not provided
  if (!planId) {
    return false;
  }

  // Look up the plan by ID in planData
  const plan = planIdToPlan[planId as keyof typeof planIdToPlan];

  // If plan is found, use its isDeveloperSignup property
  // Otherwise, assume it's not a developer signup (legacy behavior)
  return plan?.isDeveloperSignup ?? false;
};

export function getBasePlanLimitsForProducts(products: AnnualCommitOverageProductDetails[]) {
  return products.reduce((acc, product) => {
    // No annual SKU, so omit from base limits
    if (product.isUnbilled || product.displayOnly || product.monthlyBilledOnly) {
      return acc;
    }
    return {
      ...acc,
      [product.limitName]: product.freeQuantity,
    };
  }, {} as PlanLimitsType);
}

// Limits shown in checkout but not yet billed (AI runs today).
// TODO: remove when AI runs billing is released
export const getDisplayOnlyLimitNames = (
  planType: PlanTypes | null | undefined,
  versionKey?: number,
): ReadonlySet<LimitNames> => {
  const result = new Set<LimitNames>();
  if (!planType) {
    return result;
  }
  const plan = Object.values(planIdToPlan).find((p) => p.planType === planType);
  if (!plan) {
    return result;
  }
  const version = getPlanVersion(plan, versionKey);
  for (const product of version.products) {
    if (product.displayOnly) {
      result.add(product.limitName);
    }
  }
  return result;
};

export type BillingPricingInputs = {
  billingInterval: BillingIntervals | null;
  planPrice: schemas['PreviewPriceRep'] | null | undefined;
  subscription: Subscription | null | undefined;
};

export type BillingPricing = {
  proratedPrice: number;
  creditDue: number;
  prorationTimeMs: number | undefined;
  prorationIsTodayOrEarlier: boolean;
  isTrial: boolean;
  trialDaysRemaining: number;
  isActiveTrial: boolean;
  isExpiredTrial: boolean;
  userHasSubscription: boolean;
};

export function getBillingPricing({ billingInterval, planPrice, subscription }: BillingPricingInputs): BillingPricing {
  const isYearly = billingInterval === BillingIntervals.YEARLY;

  const proratedPriceCents = isYearly
    ? (planPrice?.proratedPrices?.yearly ?? 0)
    : (planPrice?.proratedPrices?.monthly ?? 0);
  const creditDueCents = isYearly ? (planPrice?.creditDue?.yearly ?? 0) : (planPrice?.creditDue?.monthly ?? 0);
  const prorationTimeSec = planPrice?.prorationTime;

  const proratedPrice = proratedPriceCents / 100;
  const creditDue = creditDueCents / 100;

  const midnightLocalTime = startOfTomorrow().getTime();
  const prorationTimeMs = prorationTimeSec ? prorationTimeSec * 1000 : undefined;
  const prorationIsTodayOrEarlier = prorationTimeMs !== undefined && prorationTimeMs < midnightLocalTime;

  const isTrialValue = subscription ? isTrial(subscription) : false;
  const { trialDaysRemaining } = subscription ? getTrialStatusInfo(subscription) : { trialDaysRemaining: 0 };
  const isActiveTrial = subscription ? isTrialValue && trialDaysRemaining > 0 : false;
  const isExpiredTrial = subscription
    ? isTrialExpired(subscription) || (isTrialValue && trialDaysRemaining <= 0)
    : false;
  const userHasSubscription = subscription ? hasSubscription(subscription) : false;

  return {
    proratedPrice,
    creditDue,
    prorationTimeMs,
    prorationIsTodayOrEarlier,
    isTrial: isTrialValue,
    trialDaysRemaining,
    isActiveTrial,
    isExpiredTrial,
    userHasSubscription,
  };
}
/**
 * Calculates the total price for a given quantity using marginal bulk pricing tiers
 * @param quantity - The total quantity to calculate pricing for
 * @param tiers - Array of pricing tiers sorted by min quantity (ascending)
 * @param blockSize - The size of each pricing block
 * @param freeQuantity - The quantity that's included for free
 * @returns The total price in cents
 */
export function calculateBulkPricing(
  quantity: number,
  tiers: BulkPricingTier[],
  blockSize: number,
  freeQuantity: number = 0,
): number {
  // If quantity is within free tier, no charge
  if (quantity <= freeQuantity) {
    return 0;
  }

  // Calculate the billable quantity (quantity above free tier)
  let totalPriceCents = 0;

  // Sort tiers by min quantity to ensure proper marginal calculation
  const sortedTiers = [...tiers].sort((a, b) => a.min - b.min);

  for (const tier of sortedTiers) {
    // Skip tiers that are below our current quantity
    if (tier.min >= quantity) {
      break;
    }

    // Calculate how much of this tier we're using
    const tierMax = tier.max === Number.MAX_SAFE_INTEGER ? quantity : tier.max;
    const quantityInThisTier = Math.max(0, Math.min(quantity, tierMax) - tier.min);

    // Only charge for quantity that's actually in this tier
    if (quantityInThisTier > 0) {
      // Calculate blocks needed for this tier and add to total
      const blocksInTier = quantityInThisTier / blockSize;
      totalPriceCents += blocksInTier * tier.pricePerBlockCents;
    }
  }

  return totalPriceCents;
}

// Checkout session calls the flag, subscription checks the provisioned limit.
export const isProductGateAllowed = (
  product: AnnualCommitOverageProductDetails,
  ctx: ProductVisibilityContext,
): boolean => {
  if (!product.gatedByFlag) {
    return true;
  }
  if (ctx.source === 'session') {
    return product.gatedByFlag();
  }
  return hasSubscriptionLimit(ctx.subscriptionLimits[product.limitName]);
};

export const getVisiblePlanProducts = (
  products: AnnualCommitOverageProductDetails[],
  ctx: ProductVisibilityContext,
): AnnualCommitOverageProductDetails[] => products.filter((product) => isProductGateAllowed(product, ctx));

export const planLimitsSubtext = (
  products: AnnualCommitOverageProductDetails[],
  billingInterval: BillingIntervals | null | undefined,
  planLimits: PlanLimitsType,
  visibility?: ProductVisibilityContext,
) => {
  // Keep `displayOnly` products in annual plan inclusions
  const visibleProducts = visibility
    ? products.filter((product) => isProductGateAllowed(product, visibility))
    : products;

  const limitEntries = visibleProducts.map((product) => {
    const limitName = product.limitName;
    const value =
      billingInterval === BillingIntervals.MONTHLY ||
      product.isUnbilled ||
      product.displayOnly ||
      product.monthlyBilledOnly
        ? product.freeQuantity
        : planLimits[limitName];
    if (value === 0 || value === null || value === undefined) {
      return null;
    }
    const displayName = getLimitDisplayName(limitName, 'plural');
    const formattedValue = formatNumber(value, {
      compactDisplay: 'short',
      notation: 'compact',
      maximumFractionDigits: 1,
    });

    return `${capitalize(displayName)} ${formattedValue}/mo`;
  });

  return limitEntries.filter((entry) => entry !== null).join('; ');
};

export const getDiscountedPrice = (planPrice: schemas['PreviewPriceRep'] | null | undefined) => {
  const discountedPriceCents = planPrice?.prices?.yearly ?? 0;
  const discountedPrice = discountedPriceCents / CENTS_PER_DOLLAR;
  const retailPrice = ((discountedPriceCents / 10) * 12) / CENTS_PER_DOLLAR;
  const discount = retailPrice - discountedPrice;

  return { discountedPrice, retailPrice, discount };
};
/**
 * Helper function to check if an object is considered "empty" based on plan limits criteria.
 * An object is considered empty if all its values are null, undefined, or 0.
 */
export const isPlanLimitsEmpty = (obj: unknown): boolean => {
  if (!obj || typeof obj !== 'object') {
    return true;
  }

  // Remove _values field before checking if empty
  const { _values, ...objWithoutValues } = obj as Record<string, unknown>;

  return Object.values(objWithoutValues).every((value) => value === null || value === undefined || value === 0);
};

/**
 * Given a modern sub, return its corresponding fe hard-coded Plan object
 * @param subscription the ModernSubscription to get Plan for
 * @returns The hardcoded frontend Plan object such as developerPlan, developer2025Plan, foundationPlan, foundation2025Plan or guardianPlan.
 */
export const getFEPlan = (subscription?: ModernSubscription) => {
  if (!subscription) {
    return undefined;
  }

  switch (subscription.planType) {
    case PlanTypes.DEVELOPER:
      return developerPlan;
    case PlanTypes.DEVELOPER2025:
      return developer2025Plan;
    case PlanTypes.FOUNDATION2023:
      return foundationPlan;
    case PlanTypes.FOUNDATION2025:
      return foundation2025Plan;
    case PlanTypes.GUARDIAN2024:
      return guardianPlan;
    default:
      return undefined;
  }
};

export function getSubscriptionComparison(
  subscription: ModernSubscription | undefined,
  planType: PlanTypes | null,
  versionKey: number | undefined,
) {
  const isSamePlanVersion =
    versionKey !== undefined && subscription?.version === versionKey && subscription?.planType === planType;
  const isPaidVersionSwitch =
    enableAgentControlCheckout() && versionKey !== undefined && hasPaidPlan(subscription) && !isSamePlanVersion;
  const isCurrentlyAnnual = subscription?.billingInterval === BillingIntervals.YEARLY;
  return { isSamePlanVersion, isPaidVersionSwitch, isCurrentlyAnnual };
}

export function shouldDisablePlanSelection(
  plan: Plan,
  subscription: ModernSubscription | undefined,
  targetVersion?: number,
): boolean {
  if (!subscription?.planType) {
    return false;
  }
  const disableRules =
    (targetVersion !== undefined ? plan.versions[targetVersion]?.disableIf : undefined) ?? plan.disableIf;
  if (!disableRules) {
    return false;
  }
  return disableRules.some(
    (rule) =>
      rule.planType === subscription.planType && (rule.version === undefined || rule.version === subscription.version),
  );
}

export function shouldDisableDevPlanSelection(plan: Plan, subscription: ModernSubscription | undefined): boolean {
  const isOnActiveTrial = subscription ? isActiveTrialWithNoPlan(subscription) : false;

  return disableFreeDevPlanSelection() && !!plan.isFreeDevPlan && isOnActiveTrial;
}
